import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, TrendingUp, Users } from "lucide-react";
import Logo, { PulseMark } from "@/components/brand/Logo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const points = [
  { icon: CalendarDays, label: "One calendar for every venue" },
  { icon: TrendingUp, label: "Revenue clarity in real time" },
  { icon: Users, label: "Employee visibility, zero chaos" },
];

export default function Login() {
  const navigate = useNavigate();

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const GoogleG = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/>
      <path fill="#FBBC05" d="M5.85 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.67-2.84Z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.67 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/>
    </svg>
  );

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
      {/* Left — brand panel (desktop) */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-pulse-navy px-14 py-12 text-white lg:flex">
        {/* soft green glow + faint watermark for depth */}
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-pulse-green/20 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-10 -right-10 opacity-[0.04]">
          <PulseMark size={340} color="#FFFFFF" />
        </div>
        <div className="grain-overlay pointer-events-none absolute inset-0" />

        <button onClick={() => navigate("/")} className="relative z-10 flex w-fit items-center gap-2 text-sm text-white/50 transition-colors hover:text-white" data-testid="login-back-home">
          <ArrowLeft size={16} /> Back to home
        </button>

        <div className="relative z-10 max-w-md">
          <Logo variant="light" size={34} />
          <h1 className="mt-10 text-[2.75rem] font-bold leading-[1.08] tracking-tight">
            The heartbeat<br />of your business.
          </h1>
          <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-white/55">
            Bookings, revenue and your team — in one calm, beautiful place built for venue owners.
          </p>
          <ul className="mt-9 space-y-3.5">
            {points.map((p) => (
              <li key={p.label} className="flex items-center gap-3 text-sm text-white/75">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/[0.06] text-pulse-green ring-1 ring-white/10">
                  <p.icon size={16} />
                </span>
                {p.label}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative z-10 text-xs text-white/30">Trusted by venue owners across India.</div>
      </div>

      {/* Right — sign in */}
      <div className="relative flex flex-col items-center justify-center bg-white px-6 py-12 sm:px-10">
        {/* mobile back link */}
        <button onClick={() => navigate("/")} className="absolute left-5 top-5 flex items-center gap-1.5 text-sm text-slate transition-colors hover:text-pulse-navy lg:hidden">
          <ArrowLeft size={16} /> Home
        </button>

        <div className="w-full max-w-sm">
          <div className="flex justify-center lg:justify-start">
            <Logo variant="dark" size={32} />
          </div>

          <h2 className="mt-10 text-center text-3xl font-bold tracking-tight text-pulse-navy lg:text-left">Welcome back</h2>
          <p className="mt-2 text-center text-slate lg:text-left">Sign in with your Google account to continue.</p>

          <button
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            className="mt-9 flex w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-charcoal shadow-pulse-sm transition-all duration-150 hover:border-slate-300 hover:shadow-pulse-md active:scale-[0.98]"
          >
            <GoogleG /> Continue with Google
          </button>

          <div className="mt-8 rounded-xl bg-cloud/70 p-4">
            <p className="text-xs leading-relaxed text-slate">
              No business ID or password needed. Pulse detects your role automatically — owners get the full dashboard, invited staff get the calendar.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
